import React, {useRef} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {Controller, useForm} from "react-hook-form";
import {z} from "zod";
import {Box, Colors, Container, CustomDialog, Tab, Tabs, Text} from "@bounce/shared/ui";
import {Button, TextField} from "../../ui/components";

export type UploadAssetResult = {name?: string; file?: File; sourceUrl?: string};

type Props = {
  onConfirm: (result: UploadAssetResult) => void;
};

const uploadAssetSchema = z
  .object({
    mode: z.enum(["file", "link"]),
    name: z.string(),
    file: z.instanceof(File).nullable(),
    sourceUrl: z.string(),
  })
  .refine((values) => (values.mode === "file" ? values.file !== null : values.sourceUrl.trim().length > 0));

type FormValues = z.infer<typeof uploadAssetSchema>;

const EMPTY_FORM: FormValues = {mode: "file", name: "", file: null, sourceUrl: ""};

const fileBaseName = (fileName: string) => fileName.replace(/\.[^.]+$/, "");

const tabSx = {
  minHeight: 40,
  textTransform: "none",
  "&.Mui-selected": {color: Colors.blue._700},
} as const;

const UploadAssetDialog = NiceModal.create(({onConfirm}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: {isValid},
  } = useForm<FormValues>({
    resolver: zodResolver(uploadAssetSchema),
    mode: "onChange",
    defaultValues: EMPTY_FORM,
  });

  const mode = watch("mode");
  const file = watch("file");

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    event.target.value = "";
    setValue("file", selected, {shouldValidate: true});
    if (selected && !getValues("name").trim()) setValue("name", fileBaseName(selected.name));
  };

  const onSubmit = handleSubmit((values) =>
    onConfirm(
      values.mode === "file"
        ? {name: values.name.trim() || undefined, file: values.file ?? undefined}
        : {name: values.name.trim() || undefined, sourceUrl: values.sourceUrl.trim()}
    )
  );

  return (
    <CustomDialog
      title="Add asset"
      forceTitle
      buttonText="Add asset"
      width="420px"
      disabled={!isValid}
      onClick={onSubmit}
    >
      <Controller
        name="name"
        control={control}
        render={({field: {ref, ...field}}) => (
          <TextField
            {...field}
            inputRef={ref}
            fullWidth
            size="small"
            label="Name"
            placeholder={mode === "file" ? "Defaults to the file name" : "Defaults to the file name in the link"}
          />
        )}
      />
      <Controller
        name="mode"
        control={control}
        render={({field}) => (
          <Tabs
            value={field.value}
            onChange={(_, value) => field.onChange(value)}
            sx={{
              minHeight: 40,
              borderBottom: `1px solid ${Colors.tertiary._300}`,
              width: "100%",
              "& .MuiTabs-indicator": {backgroundColor: Colors.blue._700},
            }}
          >
            <Tab value="file" label="Upload a file" sx={tabSx} />
            <Tab value="link" label="Link to an asset" sx={tabSx} />
          </Tabs>
        )}
      />
      {mode === "file" ? (
        <Container sx={{gap: "8px", width: "100%", alignItems: "start"}}>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileSelected} />
          <Button buttonType="contained-secondary" size="small" onClick={() => fileInputRef.current?.click()}>
            {file ? "Choose a different file" : "Choose a file"}
          </Button>
          <Text size={13} sx={{color: file ? Colors.grey._900 : Colors.grey._500}}>
            {file ? file.name : "No file selected"}
          </Text>
        </Container>
      ) : (
        <Box sx={{width: "100%"}}>
          <Controller
            name="sourceUrl"
            control={control}
            render={({field: {ref, ...field}}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                label="Asset link"
                placeholder="https://..."
              />
            )}
          />
          <Text size={13} sx={{color: Colors.grey._500, mt: 1, display: "block"}}>
            The image is downloaded once and re-hosted by Bounce, so the original link can go away safely.
          </Text>
        </Box>
      )}
    </CustomDialog>
  );
});

export default UploadAssetDialog;
